import React from 'react';

const SkeletonBlock = ({ dark, className = '', style }) => (
    <div
        aria-hidden="true"
        className={`motion-reduce:animate-none animate-pulse rounded ${dark ? 'bg-[#333]' : 'bg-slate-200'} ${className}`}
        style={style}
    />
);

export function TableSkeletonRows({ isDarkMode = true, columns = 5, rows = 7 }) {
    const columnCount = Math.max(1, columns);
    return Array.from({ length: rows }, (_, rowIndex) => (
        <tr key={`loading-row-${rowIndex}`} aria-hidden="true">
            {Array.from({ length: columnCount }, (_, columnIndex) => (
                <td key={`loading-cell-${rowIndex}-${columnIndex}`} className="px-4 py-3">
                    <SkeletonBlock
                        dark={isDarkMode}
                        className={`${columnIndex === 0 ? 'w-3/4' : columnIndex === 1 ? 'w-2/3' : 'w-1/2'} ${columnIndex > 0 ? 'ml-auto' : ''} h-3`}
                    />
                </td>
            ))}
        </tr>
    ));
}

export default function LoadingSkeleton({ isDarkMode = true, variant = 'content', className = '' }) {
    const dark = isDarkMode;
    const text = dark ? 'bg-white/[0.10]' : 'bg-slate-200';
    const border = dark ? 'border-white/[0.08]' : 'border-slate-200';
    const card = dark ? 'bg-[#0e1722]' : 'bg-white';
    const pageClass = variant === 'screen' ? 'min-h-screen p-5 md:p-7' : 'min-h-[320px]';

    return (
        <div
            role="status"
            aria-label="Inhalt wird geladen"
            aria-busy="true"
            className={`${dark ? 'text-slate-200' : 'text-slate-700'} ${pageClass} ${className}`}
        >
            {variant === 'screen' && (
                <div className={`mb-6 flex items-center justify-between border-b pb-4 ${border}`}>
                    <SkeletonBlock dark={dark} className="h-7 w-36" />
                    <div className="flex gap-2">
                        <SkeletonBlock dark={dark} className="h-9 w-24" />
                        <SkeletonBlock dark={dark} className="h-9 w-28" />
                    </div>
                </div>
            )}
            <div className="space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="space-y-2">
                        <SkeletonBlock dark={dark} className={`h-5 w-44 ${text}`} />
                        <SkeletonBlock dark={dark} className="h-3 w-64 max-w-[70vw]" />
                    </div>
                    <div className="flex gap-2">
                        <SkeletonBlock dark={dark} className="h-9 w-24" />
                        <SkeletonBlock dark={dark} className="h-9 w-28" />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                    {Array.from({ length: 4 }, (_, index) => (
                        <div key={`loading-kpi-${index}`} className={`rounded-xl border p-4 ${border} ${card}`}>
                            <SkeletonBlock dark={dark} className="h-3 w-24" />
                            <SkeletonBlock dark={dark} className="mt-4 h-7 w-32 max-w-full" />
                            <SkeletonBlock dark={dark} className="mt-3 h-3 w-20" />
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                    <div className={`rounded-xl border p-5 xl:col-span-2 ${border} ${card}`}>
                        <SkeletonBlock dark={dark} className="h-4 w-40" />
                        <SkeletonBlock dark={dark} className="mt-2 h-3 w-56 max-w-full" />
                        <div className="mt-6 flex h-44 items-end gap-3 border-b px-2 pb-2">
                            {[42, 68, 50, 82, 58, 72, 45, 90, 62, 76, 52, 70].map((height, index) => (
                                <SkeletonBlock key={`loading-bar-${index}`} dark={dark} className="flex-1 rounded-t-sm rounded-b-none" style={{ height: `${height}%` }} />
                            ))}
                        </div>
                    </div>
                    <div className={`rounded-xl border p-5 ${border} ${card}`}>
                        <SkeletonBlock dark={dark} className="h-4 w-32" />
                        <div className="mt-5 space-y-4">
                            {Array.from({ length: 4 }, (_, index) => (
                                <div key={`loading-summary-${index}`} className="flex items-center gap-3">
                                    <SkeletonBlock dark={dark} className="h-8 w-8 rounded-full" />
                                    <div className="min-w-0 flex-1 space-y-2">
                                        <SkeletonBlock dark={dark} className="h-3 w-3/4" />
                                        <SkeletonBlock dark={dark} className="h-2.5 w-1/2" />
                                    </div>
                                    <SkeletonBlock dark={dark} className="h-3 w-12" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className={`overflow-hidden rounded-xl border ${border} ${card}`}>
                    <div className={`flex items-center justify-between border-b p-4 ${border}`}>
                        <SkeletonBlock dark={dark} className="h-4 w-36" />
                        <SkeletonBlock dark={dark} className="h-8 w-24" />
                    </div>
                    <div className="space-y-4 p-4">
                        {Array.from({ length: 4 }, (_, index) => (
                            <div key={`loading-list-${index}`} className="flex items-center gap-4">
                                <SkeletonBlock dark={dark} className="h-3 w-1/4" />
                                <SkeletonBlock dark={dark} className="h-3 w-1/5" />
                                <SkeletonBlock dark={dark} className="ml-auto h-3 w-16" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <span className="sr-only">Wird geladen …</span>
        </div>
    );
}
